import { postReq } from "./request";
import { bestMatch, termWords } from "./fuzzyMatch";
import { SearchResponseBody } from "../constants/searchCategories";

export type RecordKind = "provider" | "client" | "company" | "lead" | "contact";

export interface SimilarRecord {
    kind: RecordKind;
    id: number;
    name: string;
    url: string;
    parent: string | null;
    /** The same name; false = the closest one, to show as predicted. */
    exact: boolean;
}

const KIND_BY_PATH: Array<[RegExp, RecordKind]> = [
    [/^\/provider\/(\d+)$/, "provider"],
    [/^\/client\/(\d+)$/, "client"],
    [/^\/company\/(\d+)$/, "company"],
    [/^\/lead\/(\d+)$/, "lead"],
    [/^\/contact\/(\d+)$/, "contact"],
];

/** Words of a request that never name a record. */
const FILLER = new Set(["this", "that", "these", "those", "please", "into", "onto", "with", "from", "attach", "attached", "update", "upload",
    "list", "lists", "sheet", "rate", "rates", "price", "prices", "quote", "fleet", "file", "document", "licence", "license", "profile", "supplier", "their", "here"]);

/**
 * The provider, client, company or lead already in CRX that a name read by
 * code most likely is — the same name, else the closest ("predicted"). The
 * search runs as the person, so only records they can open come back.
 * @param name - The company name read ("App Review Demo Provider").
 * @param kinds - Which records count.
 * @param skipUrl - A record to leave out (the one being edited).
 */
export const findSimilarRecord = async (name: string, kinds: RecordKind[], skipUrl?: string): Promise<SimilarRecord | null> => {
    const wanted = String(name ?? "").trim();
    if (wanted.length < 3) return null;
    // the two longest telling words as extra searches ("add this to Gulf Shield" → "shield", "gulf")
    const words = Array.from(new Set(termWords(wanted).filter((word) => word.length >= 4 && !FILLER.has(word))))
        .sort((a, b) => b.length - a.length)
        .slice(0, 2);
    const found = new Map<string, SimilarRecord>();
    for (const text of [wanted, ...words]) {
        const response = await Promise.resolve(postReq<SearchResponseBody>("extra/quick-search", { text })).catch(() => null);
        (response?.status === 200 ? response.body?.data ?? [] : []).forEach((item) => {
            for (const [pattern, kind] of KIND_BY_PATH) {
                const match = pattern.exec(item.url ?? "");
                if (match && kinds.includes(kind) && item.url !== skipUrl) {
                    found.set(item.url, { kind, id: Number(match[1]), name: item.name, url: item.url, parent: item.parent || null, exact: false });
                }
            }
        });
    }
    const records = Array.from(found.values());
    const hit = bestMatch(wanted, records.map((record) => record.name), 0.6);
    const record = hit ? records.find((candidate) => candidate.name === hit.value) : null;
    return record && hit ? { ...record, exact: hit.exact } : null;
};
